import React, { useState, useRef } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { uploadDishImage, uploadARModel } from '../../../services/storageService';

const AddDishModal = ({ isOpen, onClose, onSave, categories, restaurantId }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ingredients: '',
    price: '',
    category: '',
    image: '',
    hasARModel: false,
    arModelUrl: '',
    status: 'draft',
    widthCm: '',
    heightCm: '',
    depthCm: '',
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [arFile, setArFile] = useState(null);
  const [arFileName, setArFileName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const imageInputRef = useRef(null);
  const arInputRef = useRef(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setUploadError('');
  };

  const handleArFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const MAX_GLB_SIZE = 500 * 1024 * 1024; // 500 MB
    if (file?.size > MAX_GLB_SIZE) {
      setUploadError('Le fichier AR est trop volumineux. La taille maximale est de 500 Mo.');
      e.target.value = '';
      return;
    }

    const ext = file?.name?.split('.')?.pop()?.toLowerCase();
    const supportedExtensions = ['glb', 'gltf', 'usdz', 'ply', 'splat'];
    if (!supportedExtensions?.includes(ext)) {
      setUploadError('Format AR non supporte. Utilisez un fichier .glb, .gltf, .usdz, .ply ou .splat.');
      e.target.value = '';
      return;
    }

    setArFile(file);
    setArFileName(file?.name);
    setUploadError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsUploading(true);
    setUploadError('');

    try {
      let imageUrl = formData?.image;
      let arModelUrl = formData?.arModelUrl;

      if (imageFile) {
        imageUrl = await uploadDishImage(imageFile, restaurantId);
      }
      if (arFile) {
        const { url } = await uploadARModel(arFile, restaurantId);
        arModelUrl = url;
      }

      onSave({ ...formData, image: imageUrl, arModelUrl });

      // Reset
      setFormData({
        name: '',
        description: '',
        ingredients: '',
        price: '',
        category: '',
        image: '',
        hasARModel: false,
        arModelUrl: '',
        status: 'draft',
        widthCm: '',
        heightCm: '',
        depthCm: '',
      });
      setImageFile(null);
      setImagePreview('');
      setArFile(null);
      setArFileName('');
    } catch (err) {
      setUploadError('Échec du téléversement du fichier. Veuillez réessayer.');
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  const frenchCategoryLabels = {
    appetizers: 'Entrées',
    mains: 'Plats principaux',
    desserts: 'Desserts',
    beverages: 'Boissons',
  };

  const categoryOptions = categories?.map(cat => ({
    value: cat?.id,
    label: frenchCategoryLabels?.[cat?.id] || cat?.name
  }));

  const statusOptions = [
    { value: 'draft', label: 'Brouillon' },
    { value: 'active', label: 'Actif' },
    { value: 'inactive', label: 'Inactif' }
  ];

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="add-dish-modal bg-card border border-border rounded-lg shadow-warm-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground">
            Ajouter un nouveau plat
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={20}
            className="touch-target"
          />
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 md:space-y-6">
          <Input
            type="text"
            label="Nom du plat"
            placeholder="Entrez le nom du plat"
            value={formData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
            required
          />

          <Input
            type="text"
            label="Description"
            placeholder="Brève description du plat"
            value={formData?.description}
            onChange={(e) => handleChange('description', e?.target?.value)}
            required
          />

          <Input
            type="text"
            label="Ingrédients"
            placeholder="Listez les ingrédients principaux"
            value={formData?.ingredients}
            onChange={(e) => handleChange('ingredients', e?.target?.value)}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="number"
              label="Prix (CAD)"
              placeholder="0.00"
              value={formData?.price}
              onChange={(e) => handleChange('price', e?.target?.value)}
              required
            />

            <Select
              label="Catégorie"
              placeholder="Sélectionnez une catégorie"
              options={categoryOptions}
              value={formData?.category}
              onChange={(value) => handleChange('category', value)}
              required
            />
          </div>

          {/* Image Upload */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Image du plat
            </label>
            <div
              className="border-2 border-dashed border-border rounded-lg p-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
              onClick={() => imageInputRef?.current?.click()}
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Aperçu"
                  className="w-24 h-24 object-cover rounded-lg"
                />
              ) : (
                <>
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  </div>
                  <p className="text-sm text-muted-foreground">Cliquez pour choisir une image</p>
                  <p className="text-xs text-muted-foreground">JPG, PNG, WEBP — max 10 Mo</p>
                </>
              )}
              {imageFile && (
                <p className="text-xs text-primary font-medium truncate max-w-full">{imageFile?.name}</p>
              )}
            </div>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={handleImageFileChange}
            />
          </div>

          <Select
            label="Statut"
            options={statusOptions}
            value={formData?.status}
            onChange={(value) => handleChange('status', value)}
          />

          <Checkbox
            label="Modèle AR disponible"
            description="Activer la visualisation en réalité augmentée pour ce plat"
            checked={formData?.hasARModel}
            onChange={(e) => handleChange('hasARModel', e?.target?.checked)}
          />

          {formData?.hasARModel && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  Fichier du modèle AR
                </label>
                <div
                  className="border-2 border-dashed border-border rounded-lg p-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
                  onClick={() => arInputRef?.current?.click()}
                >
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  </div>
                  {arFileName ? (
                    <p className="text-xs text-primary font-medium truncate max-w-full">{arFileName}</p>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">Cliquez pour choisir un fichier AR</p>
                      <p className="text-xs text-muted-foreground">.glb, .gltf, .usdz, .ply, .splat — max 500 Mo</p>
                    </>
                  )}
                </div>
                <input
                  ref={arInputRef}
                  type="file"
                  accept=".glb,.gltf,.usdz,.ply,.splat,model/gltf-binary,model/gltf+json,model/vnd.usdz+zip,application/octet-stream,application/x-binary,application/binary"
                  className="hidden"
                  onChange={handleArFileChange}
                />
              </div>

              {/* AR Dimensions */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  Dimensions réelles en AR (cm)
                </label>
                <p className="text-xs text-muted-foreground">
                  Définissez la taille exacte du plat en réalité augmentée. Laissez vide pour utiliser la taille par défaut du modèle.
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    type="number"
                    label="Largeur"
                    placeholder="ex: 25"
                    value={formData?.widthCm}
                    onChange={(e) => handleChange('widthCm', e?.target?.value)}
                    step="0.1"
                    min="0"
                  />
                  <Input
                    type="number"
                    label="Hauteur"
                    placeholder="ex: 8"
                    value={formData?.heightCm}
                    onChange={(e) => handleChange('heightCm', e?.target?.value)}
                    step="0.1"
                    min="0"
                  />
                  <Input
                    type="number"
                    label="Profondeur"
                    placeholder="ex: 25"
                    value={formData?.depthCm}
                    onChange={(e) => handleChange('depthCm', e?.target?.value)}
                    step="0.1"
                    min="0"
                  />
                </div>
              </div>
            </div>
          )}

          {uploadError && (
            <p className="text-sm text-destructive">{uploadError}</p>
          )}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-4 border-t border-border">
            <Button
              type="submit"
              variant="default"
              iconName={isUploading ? 'Loader' : 'Check'}
              iconPosition="left"
              iconSize={18}
              className="flex-1 sm:flex-none"
              disabled={isUploading}
            >
              {isUploading ? 'Téléversement...' : 'Ajouter le plat'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 sm:flex-none"
              disabled={isUploading}
            >
              Annuler
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDishModal;